import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  EventEmitter,
} from '@angular/core';

import { SnApplianceCapacityStatus, SnApplianceStatus, SnCapacity } from '@lib/api/entities';
import { FsFormatDatePipe } from '@lib/common/pipes';
import { LocaleService } from '@lib/translation';

import { formatBandwidth } from '../../helpers/utils';

@Component({
  selector: 'fs-appliance-dashboard-capacity-section',
  templateUrl: './appliance-dashboard-capacity.component.html',
  styleUrls: ['./appliance-dashboard-capacity.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ApplianceDashboardCapacityComponent implements OnChanges {
  @Input() public capacity: SnCapacity;
  @Input() public status: SnApplianceStatus;
  @Input() public updatesPending: boolean;
  @Output() public refreshNetworkSpeed = new EventEmitter<{
    onFailure: () => void;
  }>();

  private readonly DEFAULT_VALUES = {
    storageUsagePercent: 0,
    streamingUsagePercent: 0,
    capacityStatusUpdatedAt: 'N/A',
    storageInfo: '—',
    streamingInfo: '—',
    networkSpeed: '—',
    networkSpeedUpdatedAt: 'N/A',
  };
  private readonly STORAGE_UNIT = {
    TB: 'TB',
    GB: 'GB',
    MB: 'MB',
  };

  public storageUsagePercent = this.DEFAULT_VALUES.storageUsagePercent;
  public streamingUsagePercent = this.DEFAULT_VALUES.streamingUsagePercent;
  private _capacityStatusUpdatedAt = this.DEFAULT_VALUES.capacityStatusUpdatedAt;
  public storageInfo = this.DEFAULT_VALUES.storageInfo;
  public streamingInfo = this.DEFAULT_VALUES.streamingInfo;
  public networkSpeed = this.DEFAULT_VALUES.networkSpeed;
  private _networkSpeedUpdatedAt = this.DEFAULT_VALUES.networkSpeedUpdatedAt;

  private readonly _lastCheckLabel = 'Last check: ';

  public readonly SnApplianceCapacityStatus = SnApplianceCapacityStatus;

  constructor(private readonly _localeService: LocaleService) {}

  get capacityStatusTimestampToolTip() {
    return this._lastCheckLabel + this._capacityStatusUpdatedAt;
  }

  get networkSpeedTimestampToolTip() {
    return this._lastCheckLabel + this._networkSpeedUpdatedAt;
  }

  get isApplianceDown(): boolean {
    return this.status === SnApplianceStatus.Down;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.capacity?.currentValue) {
      this._updateCapacityValues();
    }
  }

  private _updateCapacityValues(): void {
    if (!this.capacity) {
      this._resetValues();
      return;
    }

    this._updateNetworkSpeed();
    this._calculateUsagePercentages();
    this._updateLastUpdateTime();
    this._updateStorageInfo();
    this._updateStreamingInfo();
  }

  private _updateNetworkSpeed(): void {
    const locale = this._localeService.getCurrentLocale();
    // Multiply by 1_000_000 to convert from Mbps to bps for formatting
    this.networkSpeed = this.capacity.networkUploadSpeed
      ? formatBandwidth(this.capacity.networkUploadSpeed * 1_000_000, 2, locale)
      : this.DEFAULT_VALUES.networkSpeed;
  }

  private _calculateUsagePercentages(): void {
    // Storage usage
    this.storageUsagePercent = this._calculatePercentage(
      this.capacity.currentStorageUsage,
      this.capacity.totalStorageCapacity,
    );

    // Streaming usage
    this.streamingUsagePercent = this._calculatePercentage(
      this.capacity.currentStreamingBandwidth,
      this.capacity.totalBandwidthCapacity,
    );
  }

  private _calculatePercentage(current: number, total: number): number {
    if (!total || total <= 0) return 0;
    return Math.min(Math.floor((current / total) * 100), 100);
  }

  private _formatDate = (date: Date): string => {
    const formatDatePipe = new FsFormatDatePipe(this._localeService);
    const dateString = formatDatePipe.transform(date, {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    } as any);
    const timeString = formatDatePipe.transform(date, {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    } as any);
    return `${dateString} @ ${timeString}`;
  };

  private _updateLastUpdateTime(): void {
    this._capacityStatusUpdatedAt = this.capacity.capacityStatusUpdatedAt
      ? this._formatDate(this.capacity.capacityStatusUpdatedAt)
      : this.DEFAULT_VALUES.capacityStatusUpdatedAt;

    this._networkSpeedUpdatedAt = this.capacity.networkSpeedUpdatedAt
      ? this._formatDate(this.capacity.networkSpeedUpdatedAt)
      : this.DEFAULT_VALUES.networkSpeedUpdatedAt;
  }

  private _updateStorageInfo(): void {
    if (!this.capacity.totalStorageCapacity) {
      this.storageInfo = this.DEFAULT_VALUES.storageInfo;
      return;
    }

    const freeStorageTB = this._bytesToTB(
      Math.max(this.capacity.totalStorageCapacity - this.capacity.currentStorageUsage, 0),
    );
    const totalStorageTB = this._bytesToTB(this.capacity.totalStorageCapacity);

    const formattedFree = this._localeService.formatNumber(freeStorageTB, 2, 2);
    const formattedTotal = this._localeService.formatNumber(totalStorageTB, 2, 2);
    
    this.storageInfo = `${formattedFree} ${this.STORAGE_UNIT.TB} FREE OF ${formattedTotal} ${this.STORAGE_UNIT.TB}`;
  }

  private _updateStreamingInfo(): void {
    if (!this.capacity.totalBandwidthCapacity) {
      this.streamingInfo = this.DEFAULT_VALUES.streamingInfo;
      return;
    }

    const locale = this._localeService.getCurrentLocale();
    const freeStreaming = formatBandwidth(
      Math.max(this.capacity.totalBandwidthCapacity - this.capacity.currentStreamingBandwidth, 0),
      2,
      locale,
    );
    const totalStreaming = formatBandwidth(this.capacity.totalBandwidthCapacity, 2, locale);
    this.streamingInfo = `${freeStreaming} FREE OF ${totalStreaming}`;
  }

  private _resetValues(): void {
    this.storageUsagePercent = this.DEFAULT_VALUES.storageUsagePercent;
    this.streamingUsagePercent = this.DEFAULT_VALUES.streamingUsagePercent;
    this._capacityStatusUpdatedAt = this.DEFAULT_VALUES.capacityStatusUpdatedAt;
    this.storageInfo = this.DEFAULT_VALUES.storageInfo;
    this.streamingInfo = this.DEFAULT_VALUES.streamingInfo;
    this.networkSpeed = this.DEFAULT_VALUES.networkSpeed;
    this._networkSpeedUpdatedAt = this.DEFAULT_VALUES.networkSpeedUpdatedAt;
  }

  /**
   * Converts bytes to terabytes using binary conversion (1024-based) used for Storage Capacity indicator only.
   * Note: This uses binary calculation (KiB, MiB, GiB, TiB) but displays decimal unit symbols (GB, TB) to match the
   * Core UI display that uses a python library which does the same.
   *
   * @param bytes The number of bytes to convert
   * @returns The equivalent value in terabytes (TiB)
   */
  private _bytesToTB(bytes: number): number {
    const BYTES_PER_KB: number = 1024;
    const BYTES_PER_MB: number = BYTES_PER_KB * 1024;
    const BYTES_PER_GB: number = BYTES_PER_MB * 1024;
    const BYTES_PER_TB: number = BYTES_PER_GB * 1024;

    return bytes / BYTES_PER_TB;
  }
  public onNetworkReload(event: { onFailure: () => void }) {
    this.refreshNetworkSpeed.emit(event);
  }
}
